import {useEffect} from 'react';

import {
  CircularProgress,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import {
  Form,
  Grid,
  Input,
  Stack,
} from '@zoom/react-ui';
import {IconEyeHide, IconEyeShow, IconQuestionCircleFill} from '@zoom/sun-ui-icon';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {ZmPrimaryBtn, ZmTertiaryBtn} from 'Components/ZmButtons';
import {ZmDialog} from 'Components/ZmDialogs';
import {connectAccount} from 'Store/IntegrationStore';
import {openUrlInBrowser} from 'Utils/ZoomClientUtils';

const ConnectThirdPartyAccountDialog = ({
  accountEmail,
  emailPlaceholder,
  helpLink,
  helperText,
  isSubmitting,
  onClose,
  onSuccess,
  open,
  passwordPlaceholder,
  passwordValidationRegex,
  provider,
  title,
}) => {
  const dispatch = useDispatch();
  const {t} = useTranslation();


  const [form] = Form.useForm({
    initialValues: {
      email: accountEmail || '',
      password: '',
    },
  });

  useEffect(() => {
    // when re-authorize, the email is fixed, and user needs to re-enter the new password
    if (accountEmail) {
      form.setFieldsValue({email: accountEmail});
    }
  }, [accountEmail, form]);

  const onFinish = async (values) => {
    const thirdPartyAccount = {
      mail: values.email,
      password: values.password,
      service: provider,
    };
    const {meta} = await dispatch(
      connectAccount(thirdPartyAccount)
    );
    if (meta.requestStatus === 'fulfilled') {
      onSuccess?.(thirdPartyAccount);
    }
  };

  const passwordValidator = (_, value) => {
    // Extract password from the values object if needed
    const passwordValue = typeof value === 'object' && value?.password ? value.password : value;

    if (!passwordValue) {
      return Promise.reject(new Error(t('customFields.required')));
    }

    // Only validate format if a regex is provided
    if (passwordValidationRegex && !passwordValidationRegex.test(passwordValue)) {
      if (!helperText) {
        console.warn( // helper text should be provided when regex is provided
          'passwordValidationRegex provided without helperText. ' +
          'Helper text is required to explain password format to users.'
        );
        return Promise.reject(new Error('')); // Show validation error without message
      }
      return Promise.reject(new Error(helperText));
    }

    return Promise.resolve();
  };
  return (
    <ZmDialog open={open}>
      <DialogTitle sx={{
        padding: '24px 24px 16px 24px !important',
        lineHeight: '24px',
      }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{paddingTop: '8px', paddingBottom: '24px'}}>
        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          autoComplete="off"
        >
          <Grid container columns={12} spacing={0}>
            <Grid item xs={12}>
              <Form.Item
                label={t('common.email')}
                name="email"
                rules={[
                  {required: true, message: t('customFields.required')},
                  {type: 'email', message: t('booking.warningInvalidEmail')},
                ]}
              >
                <Input
                  autoFocus
                  placeholder={emailPlaceholder || t('common.email')}
                  disabled={!!accountEmail}
                />
              </Form.Item>
            </Grid>

            <Grid item xs={12}>
              <Form.Item
                label={
                  <span style={{display: 'inline-flex', alignItems: 'center', gap: '4px', lineHeight: 'normal'}}>
                    {passwordPlaceholder || t('integration.password')}
                    <span style={{color: '#ff4d4f'}}>*</span>
                    {helpLink && (
                      <IconButton
                        sx={{width: '16px', height: '16px', padding: 0, minWidth: 'unset'}}
                        onClick={() => openUrlInBrowser(helpLink)}
                      >
                        <IconQuestionCircleFill size={12} />
                      </IconButton>
                    )}
                  </span>
                }
                name="password"
                required={false}
                rules={[{validator: passwordValidator}]}
              >
                <Input.Password
                  placeholder={passwordPlaceholder || t('integration.password')}
                  data-testid="password-input"
                  visibilityToggle={{
                    visible: (visible) => (visible ? <IconEyeHide size={15} /> : <IconEyeShow size={15} />),
                  }}
                />
              </Form.Item>
            </Grid>
          </Grid>

          <Form.Item sx={{marginTop: '8px !important', marginBottom: '24px !important'}}>
            <Stack sx={{width: '100%'}} gap="10px" direction="row" justifyContent="flex-end">
              <ZmTertiaryBtn
                onClick={onClose}
                disabled={isSubmitting}
                size="small"
              >
                {t('common.cancel')}
              </ZmTertiaryBtn>
              <ZmPrimaryBtn
                onClick={() => form.submit()}
                disabled={isSubmitting}
                size="small"
              >
                {isSubmitting && <CircularProgress size={20} />}
                {t('integration.connect')}
              </ZmPrimaryBtn>
            </Stack>
          </Form.Item>
        </Form>
      </DialogContent>
    </ZmDialog>
  );
};
export default ConnectThirdPartyAccountDialog;
